import { createDriver as createAxiosDriver } from '@redux-requests/axios';
import {
  abortRequests,
  Driver,
  getQuery,
  handleRequests,
} from '@redux-requests/core';
import { createDriver } from '@redux-requests/promise';
import { combineReducers, configureStore } from '@reduxjs/toolkit';
import axios from 'axios';
import { connectRouter, routerMiddleware } from 'connected-react-router';
import { i18nSlice } from 'modules/i18n/i18nSlice';
import { LAYOUT_STATE_NAME, layoutReducer } from 'modules/layout/store/layout';
import { persistReducer, persistStore } from 'redux-persist';
import createSagaMiddleware from 'redux-saga';
import { setAccount } from '../modules/account/store/actions/setAccount';
import { extractMessage } from '../modules/common/utils/extractError';
import { historyInstance } from '../modules/common/utils/historyInstance';
import { NotificationActions } from 'modules/notification/store/NotificationActions';
import { notificationSlice } from '../modules/notification/store/notificationSlice';
import { rootSaga } from './rootSaga';
import { i18nPersistConfig } from './webStorageConfigs';
import {
  BlockchainNetworkId,
  DefaultTokenSymbol,
  ZERO_ADDRESS,
} from '../modules/common/conts';
import { Address } from '../modules/common/types/unit';
import { TokenSymbol } from '../modules/common/types/TokenSymbol';
import { disconnect } from 'modules/account/store/actions/disconnect';
import { getChainId } from 'modules/common/utils/localStorage';
import { getNotWeb3WalletInfo } from 'modules/account/hooks/useWeb3React';
import { getTimestamp } from 'modules/common/utils/localStorage';
import {
  getJWTToken
} from 'modules/common/utils/localStorage';

type MainApiDriverName =
  | 'mainApiEthMainnet';

const chainToMainApiDriver: {
  [key in BlockchainNetworkId]: MainApiDriverName | undefined;
} = {
  [BlockchainNetworkId.mainnet]: 'mainApiEthMainnet',
  [BlockchainNetworkId.ropsten]: undefined,
  [BlockchainNetworkId.goerli]: undefined,
  [BlockchainNetworkId.baseTestnet]: 'mainApiEthMainnet',
  [BlockchainNetworkId.baseMainnet]: 'mainApiEthMainnet',
};

function getMainApiDriverName(chainId: BlockchainNetworkId): MainApiDriverName {
  return chainToMainApiDriver[chainId] || 'mainApiEthMainnet';
}

function getAirdropMainApiDriverName(): MainApiDriverName {
  return 'mainApiEthMainnet';
}

type NftViewApiDriverName =
  | 'nftViewApiEthMainnet';


type NftView2ApiDriverName =
  | 'nftView2ApiEthMainnet'
  | 'nftView2ApiEthRinkeby'
  | 'nftView2ApiMatic'
  | 'mainApiBasMainnet';



export type DriverName =
  | MainApiDriverName
  | NftViewApiDriverName
  | NftView2ApiDriverName;

export function getTokenByDriver(
  driverName: DriverName,
  unitAddress?: Address,
) {
  if (
    driverName === 'mainApiEthMainnet'
  ) {
    if (unitAddress === ZERO_ADDRESS) {
      return TokenSymbol.ETH;
    }
  }
  return DefaultTokenSymbol;
}

const mainAxios = {
  mainApiEthMainnet: axios.create({
    baseURL: process.env.REACT_APP_API_BASE_ETH_MAINNET,
  }),
  mainApiEthRinkeby: axios.create({
    baseURL: process.env.REACT_APP_API_BASE_RINKEBY,
  }),
  mainApiSmartchain: axios.create({
    baseURL: process.env.REACT_APP_API_BASE_BSC_MAINNET,
  }),
  mainApiSmartTestChain: axios.create({
    baseURL: process.env.REACT_APP_API_BASE_BSC_TEST,
  }),
  mainApiHeco: axios.create({
    baseURL: process.env.REACT_APP_API_BASE_HECO,
  }),
  mainApiMatic: axios.create({
    baseURL: process.env.REACT_APP_API_BASE_MATIC,
  }),
  mainApiAirdrop: axios.create({
    baseURL: process.env.REACT_APP_API_BASE_AIRDROP,
  }),
  mainApiAnkr: axios.create({
    baseURL: process.env.REACT_APP_API_BASE_ANKR,
  }),
  mainApiBas: axios.create({
    baseURL: process.env.REACT_APP_API_BASE_BAS,
  }),
  mainApiEdge: axios.create({
    baseURL: process.env.REACT_APP_API_BASE_EDGE,
  }),
  mainApiBasMainnet: axios.create({
    baseURL: process.env.REACT_APP_API_BASE_BAS_MAINNET,
  }),
};

type mainApiDriversType = keyof typeof mainAxios;

const mainApiDrivers: { [key in mainApiDriversType]: Driver } = {
  ...mainAxios,
};
Object.keys(mainAxios).forEach(key => {
  const initAxios = mainAxios[key as mainApiDriversType];
  initAxios.interceptors.response.use(response => {
    // token invalid
    // console.log(response)
    if ([-1, -2, 401].includes(response.data?.errorCode ?? response.data?.code)) {
      store.dispatch(abortRequests());
      store.dispatch(disconnect());
      window.localStorage.clear();
      window.location.reload();
    }
    return response;
  });
  mainApiDrivers[key as mainApiDriversType] = createAxiosDriver(initAxios);
});

const { requestsReducer, requestsMiddleware } = handleRequests({
  driver: {
    default: createDriver({
      processResponse: response => ({ data: response }),
    }),
    ...mainApiDrivers,
    nftViewApiEthMainnet: createAxiosDriver(
      axios.create({
        baseURL: process.env.REACT_APP_FANGIBLE_URL_ETH_MAINNET,
      }),
    ),
    nftViewApiEthRinkeby: createAxiosDriver(
      axios.create({
        baseURL: process.env.REACT_APP_FANGIBLE_URL_RINKEBY,
      }),
    ),
    nftViewApiSmartchain: createAxiosDriver(
      axios.create({
        baseURL: process.env.REACT_APP_API_BASE_BSC_MAINNET,
      }),
    ),
    nftViewApiSmartchainTest: createAxiosDriver(
      axios.create({
        baseURL: process.env.REACT_APP_FANGIBLE_URL_BSC_TEST,
      }),
    ),
    nftViewApiHeco: createAxiosDriver(
      axios.create({
        baseURL: process.env.REACT_APP_FANGIBLE_URL_HECO,
      }),
    ),
    nftViewApiMatic: createAxiosDriver(
      axios.create({
        baseURL: process.env.REACT_APP_FANGIBLE_URL_MATIC,
      }),
    ),
    nftView2ApiEthMainnet: createAxiosDriver(
      axios.create({
        baseURL: process.env.REACT_APP_NFTVIEW_URL_V2_ETH_MAINNET,
      }),
    ),
    nftView2ApiEthRinkeby: createAxiosDriver(
      axios.create({
        baseURL: process.env.REACT_APP_NFTVIEW_URL_V2_RINKEBY,
      }),
    ),
    nftView2ApiSmartchain: createAxiosDriver(
      axios.create({
        baseURL: process.env.REACT_APP_API_BASE_BSC_MAINNET,
      }),
    ),
    nftView2ApiSmartchainTest: createAxiosDriver(
      axios.create({
        baseURL: process.env.REACT_APP_NFTVIEW_URL_V2_BSC_TEST,
      }),
    ),
    nftView2ApiHeco: createAxiosDriver(
      axios.create({
        baseURL: process.env.REACT_APP_NFTVIEW_URL_V2_HECO,
      }),
    ),
    nftView2ApiMatic: createAxiosDriver(
      axios.create({
        baseURL: process.env.REACT_APP_NFTVIEW_URL_V2_MATIC,
      }),
    ),
  },
  onRequest: (request, action, store) => {
    const rootState: RootState = store.getState();

    const { data } = getQuery(rootState, {
      type: setAccount.toString(),
      action: setAccount,
    });

    const chainId =
      data?.chainId ?? getChainId() ?? getNotWeb3WalletInfo()?.chainId;
    const timestamp = getTimestamp() ?? data?.timestamp ?? '';
    const address = data?.address ?? '';

    // console.log('chainId', chainId);

    if (action.meta?.driver === 'axios') {
      action.meta = {
        ...action.meta,
        driver: getMainApiDriverName(chainId),
      };
    } else if (action.meta?.driver === 'airdropAxios') {
      action.meta = {
        ...action.meta,
        driver: getAirdropMainApiDriverName(),
      };
    }
    // TODO Throw exception if auth and no token?
    const token: string = getJWTToken() ?? '';
    if (action.meta?.auth) {
      return {
        ...request,
        headers: {
          ...request.headers,
          token: token || data?.token || '',
          signature: token || data?.token || '',
          timestamp: timestamp,
          cryptoAddress: address ?? '',
        },
      };
    }
    return request;
  },
  onError: (error, action, store) => {
    console.log("store", JSON.stringify(store))
    console.log("error.response.status", JSON.stringify(error.response.status))
    console.log('error.response', JSON.stringify(error.response))
    if (error && error.response && (error.response.status === 401)) {
      store.dispatch(abortRequests());
      store.dispatch(disconnect());
      window.location.reload();
    }

    if (!action.meta?.suppressErrorNotification && (extractMessage(error) !== "Error: data not found")) {
      console.log(extractMessage(error));
      store.dispatch(
        NotificationActions.showNotification({
          message: extractMessage(error),
          severity: 'error',
        }),
      );
    }
    throw error;
  },
});

const sagaMiddleware = createSagaMiddleware();

const rootReducer = combineReducers({
  i18n: persistReducer(i18nPersistConfig, i18nSlice.reducer),
  requests: requestsReducer,
  router: connectRouter(historyInstance),
  notifications: notificationSlice.reducer,
  [LAYOUT_STATE_NAME]: layoutReducer,
});

export const store = configureStore({
  reducer: rootReducer,
  middleware: [
    ...requestsMiddleware,
    routerMiddleware(historyInstance),
    sagaMiddleware,
  ],
});

export const persistor = persistStore(store);

sagaMiddleware.run(rootSaga);

export type AppDispatch = typeof store.dispatch;
export type RootState = ReturnType<typeof store.getState>;
